import { Button } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import UsersCombobox from '@/components/general/UsersCombobox';
import { PASSWORD_VALIDATE_REGEX, PHONE_VALIDATE_REGEX, ROUTES, SHOP_OWNER } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED, SHOP_OWNER_TYPE, USER_STATUS_TYPE } from '@/types/enums';
import { generateDefaultPassword } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface AddShopModalProps {
    onFetch: Function
}

export default observer(function AddShopModal({ onFetch }: AddShopModalProps) {
    // hooks
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();

    // validate Schema
    const validateSchema = yup.object().shape({
        shopName: yup
            .string()
            .required('form_error_validate.required'),
        taxCode: yup
            .string()
            .required('form_error_validate.required')
            .max(13, 'form_error_validate.tax_code_maxlength'),
        provinceName: yup
            .string()
            .required('form_error_validate.required'),
        provinceCode: yup
            .string()
            .required('form_error_validate.required'),
        shopOwnerType: yup
            .number()
            .oneOf(Object.keys(SHOP_OWNER).map(item => Number(item)))
            .required('form_error_validate.required'),
        ownerName: yup
            .string()
            .required('form_error_validate.required'),
        phoneNumber: yup
            .string().when('shopOwnerType', {
                is: value => value === SHOP_OWNER_TYPE.SelectUser,
                then: _ => yup.string(),
                otherwise: _ => yup.string().required('form_error_validate.required')
            }),
        defaultPassword: yup
            .string().when('shopOwnerType', {
                is: value => value === SHOP_OWNER_TYPE.SelectUser,
                then: _ => yup.string(),
                otherwise: _ => yup.string().required('form_error_validate.required').matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password')
            })
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        shopName: '',
        taxCode: '',
        provinceName: '',
        provinceCode: '',
        shopOwnerType: SHOP_OWNER_TYPE.SelectUser,
        ownerName: '',
        phoneNumber: '',
        defaultPassword: ''
    };

    const form = useForm({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const watchShopOwnerType = form.watch('shopOwnerType');

    // store
    const {
        shopStore: { createShop, keepSearchParams, paging },
        userStore: { userscombobox, getUsersCombobox },
        generalStore: { administrativeUnit },
        modalStore: { hideModal, showAlertModal }
    } = useStore();

    // lifecycle
    useEffect(() => {
        fetchData();
    }, []);

    // function
    const fetchData = async () => {
        await getUsersCombobox('', 20, 1);
    };

    const setOwnerName = (dataValue: string) => {
        form.setValue('ownerName', dataValue);
    };

    const setphoneNumber = (dataValue: string) => {
        form.setValue('phoneNumber', dataValue);
    };

    const onSubmit = async (data: FormData) => {
        const { shopName, taxCode, provinceCode, shopOwnerType, ownerName, phoneNumber, defaultPassword } = data;
        const res = await flowResult(createShop({
            shopName: shopName ?? '',
            taxCode: taxCode ?? '',
            provinceCode: provinceCode ?? '',
            shopOwnerType: shopOwnerType ?? '',
            ownerName: ownerName ?? '',
            phoneNumber: phoneNumber ?? '',
            defaultPassword: defaultPassword ?? ''
        }));
        if (res) {
            hideModal();
            showAlertModal({
                type: 'success',
                size: 'xl',
                saveButton: t('button.edit_shop_info'),
                cancelButton: t('button.cancel'),
                variantCancelBtn: 'outline',
                variantSaveBtn: 'default',
                showHeader: false,
                saveButtonClassName: 'w-64',
                cancelButtonClassName: 'w-64',
                footerClassName: 'flex items-center sm:justify-around w-full mt-5',
                content: (
                    <div className='text-center'>
                        <div className='font-bold'>{t('words_title.add_shop_success')}</div>
                        <div className='mt-1.5'>{t('sentences.add_shop_success')}</div>
                    </div>
                ),
                onSave: () => {
                    navigate(`${ROUTES.shopManagement.href}/${res.data.code}`);
                },
                onCancel: () => {
                    onFetch(keepSearchParams, paging);
                    hideModal();
                }
            });
        }
    };

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <FormField
                        control={form.control}
                        name='shopName'
                        render={({ field }) => (
                            <FormItem>
                                <div className='flex items-center gap-4'>
                                    <Label className='label-1 w-1/6' required>{t('words_title.shop_name')}</Label>
                                    <FormControl className='w-5/6'>
                                        <Input type='text' {...field} />
                                    </FormControl>
                                </div>
                                <div className='flex items-center gap-4'>
                                    <div className='w-1/6'></div>
                                    <FormMessage className='w-5/6' />
                                </div>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='taxCode'
                        render={({ field }) => (
                            <FormItem>
                                <div className='flex items-center gap-4 mt-4'>
                                    <Label className='label-1 w-1/6' required>{t('words_title.tax_code')}</Label>
                                    <FormControl className='w-5/6'>
                                        <Input
                                            maxLength={13}
                                            type='number'
                                            isTypingOnlyNumber={true}
                                            {...field}
                                        />
                                    </FormControl>
                                </div>
                                <div className='flex items-center gap-4'>
                                    <div className='w-1/6'></div>
                                    <FormMessage className='w-5/6' />
                                </div>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='provinceName'
                        render={({ field }) => (
                            <FormItem>
                                <div className='flex items-center gap-4 mt-4'>
                                    <Label className='label-1 w-1/6' required>{t('words_title.city')}</Label>
                                    <FormControl className='w-5/6'>
                                        <Combobox
                                            placeholder={t('placeholder.choose')}
                                            value={field.value}
                                            onChange={(item) => {
                                                field.onChange(item?.value);
                                                form.setValue('provinceCode', item?.code);
                                            }}
                                            items={administrativeUnit?.provinces?.map((item, index) => (
                                                {
                                                    label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                    value: item?.name,
                                                    code: item?.code
                                                }
                                            ))}
                                            required
                                            side='top'
                                        />
                                    </FormControl>
                                </div>
                                <div className='flex items-center gap-4'>
                                    <div className='w-1/6'></div>
                                    <FormMessage className='w-5/6' />
                                </div>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='shopOwnerType'
                        render={({ field }) => (
                            <FormItem>
                                <div className='flex items-center gap-4 mt-4 py-4'>
                                    <Label className='label-1 w-1/6' required>{t('words_title.shop_owner_type')}</Label>
                                    <FormControl className='w-5/6'>
                                        <RadioGroup
                                            key={field.value}
                                            className='flex items-center gap-24'
                                            onValueChange={(value) => {
                                                field.onChange(value);
                                                form.setValue('shopOwnerType', Number(value), { shouldValidate: true });
                                                form.setValue('ownerName', '');
                                                form.setValue('phoneNumber', '');
                                                form.setValue('defaultPassword', generateDefaultPassword());
                                                form.clearErrors();
                                            }}
                                            value={field.value.toString()}
                                        >
                                            {
                                                Object.keys(SHOP_OWNER).map((item, index) => (
                                                    <FormItem key={index} className='flex items-center gap-2'>
                                                        <FormControl>
                                                            <RadioGroupItem value={item} />
                                                        </FormControl>
                                                        <Label className='!my-0'>{t(`words_title.${SHOP_OWNER[item].key}`)}</Label>
                                                    </FormItem>
                                                ))
                                            }
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                                <div className='flex items-center gap-4'>
                                    <div className='w-1/6'></div>
                                    <FormMessage className='w-5/6' />
                                </div>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='ownerName'
                        render={({ field }) => (
                            <FormItem>
                                <div className='flex items-center gap-4 mt-4'>
                                    <Label className='label-1 w-1/6' required>{t('words_title.owner_name')}</Label>

                                    {
                                        watchShopOwnerType === SHOP_OWNER_TYPE.SelectUser ?
                                            (
                                                // <FormControl>
                                                //     <Select
                                                //         key={field.value}
                                                //         onValueChange={(value) => {
                                                //             field.onChange(value);
                                                //             form.setValue(
                                                //                 'phoneNumber',
                                                //                 (userscombobox && userscombobox.length > 0) ?
                                                //                     userscombobox.find(item => item.code === value)?.phoneNumber ?? '' :
                                                //                     ''
                                                //             );
                                                //         }}
                                                //         {...field}
                                                //         className='w-5/6'
                                                //     >
                                                //         <SelectTrigger>
                                                //             <SelectValue placeholder={t('placeholder.choose')}>
                                                //             </SelectValue>
                                                //         </SelectTrigger>
                                                //         <SelectContent classNameViewport='w-[var(--radix-select-trigger-width)]'>
                                                //             {userscombobox && userscombobox?.length > 0 && userscombobox.map((item, index) => (
                                                //                 <SelectItem
                                                //                     className='hover:cursor-pointer'
                                                //                     key={index}
                                                //                     value={item.code}
                                                //                 >
                                                //                     <span>{`${item.fullName} - ${item.phoneNumber}`}</span>
                                                //                 </SelectItem>
                                                //             ))}
                                                //         </SelectContent>
                                                //     </Select>
                                                // </FormControl>
                                                <>
                                                    <div className='w-5/6'>
                                                        <UsersCombobox className='' setOwnerName={setOwnerName} setphoneNumber={setphoneNumber} />
                                                    </div>
                                                </>
                                            ) :
                                            (
                                                <FormControl className='w-5/6'>
                                                    <Input
                                                        {...field}
                                                    />
                                                </FormControl>

                                            )
                                    }
                                </div>
                                <div className='flex items-center gap-4'>
                                    <div className='w-1/6'></div>
                                    <FormMessage className='w-5/6' />
                                </div>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='phoneNumber'
                        render={({ field }) => (
                            <FormItem>
                                <div className='flex items-center gap-4 mt-4'>
                                    <Label className='label-1 w-1/6' required>{t('words_title.phone_number')}</Label>
                                    <FormControl className='w-5/6'>
                                        <Input
                                            disabled={watchShopOwnerType === SHOP_OWNER_TYPE.SelectUser}
                                            maxLength={10}
                                            {...field}
                                        />
                                    </FormControl>
                                </div>
                                <div className='flex items-center gap-4'>
                                    <div className='w-1/6'></div>
                                    <FormMessage className='w-5/6' />
                                </div>
                            </FormItem>
                        )}
                    />
                    {
                        form.getValues('shopOwnerType') !== SHOP_OWNER_TYPE.SelectUser && (
                            <FormField
                                control={form.control}
                                name='defaultPassword'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center gap-4 mt-4'>
                                            <Label className='label-1 w-1/6' required>{t('words_title.default_password')}</Label>
                                            <FormControl className='w-5/6'>
                                                <Input {...field} />
                                            </FormControl>
                                        </div>
                                        <div className='flex items-center gap-4'>
                                            <div className='w-1/6'></div>
                                            <FormMessage className='w-5/6' />
                                        </div>
                                    </FormItem>
                                )}
                            />
                        )
                    }
                    <div className='my-8 flex gap-8 items-center justify-center'>
                        <Button
                            onClick={() => hideModal()}
                            type='button'
                            className='min-w-48'
                            variant='outline'
                        >
                            {t('button.cancel')}
                        </Button>
                        <Button type='submit' disabled={form.formState.isSubmitting} className='min-w-48'>{t('button.save')}</Button>
                    </div>
                </form>
            </Form>
        </div>
    );
});
